// EXTERNAL
import React, { useState } from "react";
// COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
// CONFIGS
import studio_config from "../../../../configs/studio.json";
// CONTEXT
import { useUserState } from "../../../../contexts/user.context";
import { useNotificationDispatch } from "../../../../contexts/notification.context";
import { useMediaLoadingDispatch } from "../../../../contexts/mediaLoading.context";
// FUNCTIONS
import { downloadAdformReport } from "../functions/downloadAdformReport";
import { requestReportAnalytics } from "../functions/requestReportAnalytics";
// TYPES
import { ReportingDateObject } from "../../../creative/library/types";
import { AdformReportModalCheckboxGroup } from "./AdformReportModalCheckboxGroup";
import { AdformReportDownloadButton } from "./AdformReportDownloadButton";
import { AdformReportingModalProps, AdformReportingModalInputs } from "../types";

/**
 * Component for Adform reporting modal
 **/
export function AdformReporting({ advertiserName, advertiserId, adformReportingModalOpen, adformReportingCreativeData, adformReportingModalPresetData, setAdformReportingModalOpen, setAdformReportingModalPresetData, setAdformReportingCreativeData }: AdformReportingModalProps) {
  const user = useUserState();
  const notificationDispatch = useNotificationDispatch();
  const mediaLoadingDispatch = useMediaLoadingDispatch();
  const [adformReportingModalInputs, setAdformReportingModalInputs] = useState<AdformReportingModalInputs>({ selectedDates: { start: adformReportingModalPresetData.dateRangeRestriction.start, end: adformReportingModalPresetData.dateRangeRestriction.end }, selectedMetrics: [], disabledMetrics: [] });

  /* Metadata to be used for multiple areas: type of generated report, analytics */
  const { scope, name, id, dateRangeRestriction } = adformReportingModalPresetData;
  /* The supported metrics from Adform that can be requested for the user */
  const supportedMetrics = studio_config.adservers.adform.reporting.supportedMetrics;
  /* To request data for whole campaign, assign the input start and end date to be the same as the campaign's start and end or today's date */
  const wholeCampaignSelected = adformReportingModalInputs.selectedDates.start === dateRangeRestriction.start && adformReportingModalInputs.selectedDates.end === dateRangeRestriction.end;

  const selectReportDates = (e: React.ChangeEvent<HTMLInputElement>) => {
    /* Define object mapping target IDs to property updates */
    const updates: { [key: string]: { [key: string]: string } } = {
      start: { start: e.target.value },
      end: { end: e.target.value },
      "Whole Campaign": { start: dateRangeRestriction.start, end: dateRangeRestriction.end },
    };
    /* Merge the relevant update with existing selectedDates using spread operator */
    const updatedSelectedDates = { ...adformReportingModalInputs.selectedDates, ...updates[e.target.id] };
    /* Update the selectedDates property using setAdformReportingModalInputs */
    setAdformReportingModalInputs({ ...adformReportingModalInputs, selectedDates: updatedSelectedDates });
  };

  const selectOneReportingMetric = (e: React.ChangeEvent<HTMLInputElement>) => {
    /* If the metric is already selected - remove this from the selectedMetrics array (to deselect the metric) */
    if (adformReportingModalInputs.selectedMetrics.includes(e.target.name)) {
      setAdformReportingModalInputs({
        ...adformReportingModalInputs,
        selectedMetrics: adformReportingModalInputs.selectedMetrics.filter((value) => {
          return value !== e.target.name;
        }),
      });
      /* If the metric is not selected - add it to the selectedMetrics array */
    } else if (!adformReportingModalInputs.selectedMetrics.includes(e.target.name)) {
      setAdformReportingModalInputs({ ...adformReportingModalInputs, selectedMetrics: [...adformReportingModalInputs.selectedMetrics, e.target.name] });
    }
  };

  /* Select all reporting metrics for report */
  const selectAllReportingMetrics = () => {
    /* Check which metrics are currently selected by the user. Removing any pre-selected or disabled metrics */
    const metricsSelectedByUser = adformReportingModalInputs.selectedMetrics.filter((value) => {
      return !adformReportingModalInputs.disabledMetrics.includes(value);
    });
    /* Select all or deselect all metrics. Preserving any pre-selected (selected + disabled) metrics */
    const newSelectedMetrics = studio_config.adservers.adform.reporting.supportedMetrics.filter((value) => {
      return (adformReportingModalInputs.selectedMetrics.includes(value) && adformReportingModalInputs.disabledMetrics.includes(value)) || (metricsSelectedByUser.length === 0 && !adformReportingModalInputs.disabledMetrics.includes(value));
    });
    setAdformReportingModalInputs({ ...adformReportingModalInputs, selectedMetrics: newSelectedMetrics });
  };

  /* Clear data selected and close reporting modal */
  const clearAndCloseReportingModal = () => {
    setAdformReportingModalOpen(false);
    setAdformReportingModalPresetData({ scope: "", name: "", id: "", dateRangeRestriction: { start: "", end: "" } });
    setAdformReportingCreativeData({ id: "", brandName: "", format: "", hasVideo: false });
  };

  /* Request to download report */
  const handleRequestAdformReport = async (scope: string, name: string, id: string, selectedDates: ReportingDateObject, selectedMetrics: string[]) => {
    await downloadAdformReport({ scope, id, metrics: selectedMetrics, dateFrom: selectedDates.start, dateTo: selectedDates.end, name, user, notificationDispatch, mediaLoadingDispatch });
    clearAndCloseReportingModal();
    requestReportAnalytics(user, advertiserId, advertiserName, scope, id, name, adformReportingCreativeData, selectedMetrics);
  };

  return (
    <>
      {adformReportingModalOpen && (
        <ComponentLibrary.PCDialogueBox open={true} onClose={clearAndCloseReportingModal} hideInitialFocus={true}>
          <ComponentLibrary.PCContainerMain>
            <p className="text-size-5 color-neutral-5 platform-font ma0 fw5">{`Request Report`}</p>
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
            <p className="text-size-3 color-neutral-5 platform-font ma0 fw5">
              {`${scope.charAt(0).toUpperCase() + scope.slice(1)}: `}
              <span className="fw1">{`${name}`}</span>
            </p>
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
            <p className="text-size-3 color-neutral-5 platform-font ma0 fw5">{`Date range`}</p>
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={3} />
            <ComponentLibrary.PCDatePicker startDate={{ value: adformReportingModalInputs.selectedDates.start, min: dateRangeRestriction.start, max: dateRangeRestriction.end, onChange: selectReportDates }} endDate={{ value: adformReportingModalInputs.selectedDates.end, min: dateRangeRestriction.start, max: dateRangeRestriction.end, onChange: selectReportDates }}></ComponentLibrary.PCDatePicker>
            <ComponentLibrary.PCCheckBox value={"Whole Campaign"} checked={wholeCampaignSelected} disabled={wholeCampaignSelected} handleChange={selectReportDates} />
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
            <p className="text-size-3 color-neutral-5 platform-font ma0 fw5">{`Metrics`}</p>
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={2} />
            <AdformReportModalCheckboxGroup supportedMetrics={supportedMetrics} adformReportingModalInputs={adformReportingModalInputs} selectOneReportingMetric={selectOneReportingMetric} selectAllReportingMetrics={selectAllReportingMetrics} />
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={2} />
            <AdformReportDownloadButton adformReportingModalInputs={adformReportingModalInputs} scope={scope} dateRangeRestriction={dateRangeRestriction} handleRequestAdformReport={handleRequestAdformReport} name={name} id={id} user={user} notificationDispatch={notificationDispatch} />
          </ComponentLibrary.PCContainerMain>
        </ComponentLibrary.PCDialogueBox>
      )}
    </>
  );
}
